import React, { useState } from 'react';

const CATEGORIES = [
  'Furniture',
  'Lighting',
  'Plumbing / Fixtures',
  'Tile & Stone',
  'Window Treatments',
  'Softgoods',
  'Millwork',
  'Hardware',
  'Appliances',
  'Art & Objects',
  'Paint & Wallpaper',
  'Finish Schedule',
];

const APPROVED_STATUSES = [
  'Client Approved',
  'Payment Received',
  'Ordered',
  'Received',
];

const STATUS_COLORS = {
  "Proposed":"#f5f5f5","Requested":"#e8f0fe","Quoted":"#fff3e0",
  "Approved":"#e8f5e9","Sent to Austin":"#e3f2fd","Proposal Received":"#fce4ec",
  "Client Approved":"#f3e5f5","Payment Received":"#e0f2f1",
  "Ordered":"#ede7f6","Received":"#e0f7fa"
};
const STATUS_TEXT = {
  "Proposed":"#555","Requested":"#1a56db","Quoted":"#e65100",
  "Approved":"#2e7d32","Sent to Austin":"#0277bd","Proposal Received":"#c2185b",
  "Client Approved":"#6a1b9a","Payment Received":"#00695c",
  "Ordered":"#4527a0","Received":"#006064"
};

export default function ScheduleGenerator({ items, projects }) {
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Lighting');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewItems, setPreviewItems] = useState(null);

  const inp = {
    padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6,
    fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff',
    minWidth: 200,
  };

  // Get all items for the selected schedule/project (regardless of status)
  function getAllScheduleItems() {
    return items.filter(item => {
      if (item.parentId) return false;
      if (selectedProject && item.project !== selectedProject) return false;
      if (item.schedule !== selectedCategory) return false;
      return true;
    });
  }

  // Get approved items for PDF generation
  function getFilteredItems() {
    return getAllScheduleItems().filter(item => APPROVED_STATUSES.includes(item.status));
  }

  // For Window Treatments: group components by assemblyName into one row per assembly
  function buildWTAssemblyRows(wtItems) {
    const assemblies = {};
    const order = [];
    wtItems.forEach(item => {
      const key = item.assemblyName || item.id;
      if (!assemblies[key]) { assemblies[key] = []; order.push(key); }
      assemblies[key].push(item);
    });
    return order.map(key => {
      const comps = assemblies[key];
      const first = comps[0];
      const fabric   = comps.find(c => c.childType === 'Fabric');
      const workroom = comps.find(c => c.childType === 'Workroom');
      const hardware = comps.find(c => c.childType === 'Hardware');
      const wSpecs   = workroom?.specs || {};
      const fSpecs   = fabric?.specs || {};
      const hSpecs   = hardware?.specs || {};
      const fabricStr = [
        fabric?.vendor,
        fSpecs.collection,
        fSpecs.patternColorway,
        fSpecs.colorway,
      ].filter(Boolean).join(' · ');
      const hardwareStr = [
        hardware?.vendor,
        hSpecs.hwType,
        hSpecs.finish,
      ].filter(Boolean).join(' · ');
      const statuses = comps.map(c => c.status).filter(Boolean);
      const allDone = statuses.every(s => APPROVED_STATUSES.includes(s));
      return {
        id: key,
        roomName: first.roomName,
        roomNumber: first.roomNumber,
        project: first.project,
        assemblyName: key !== first.id ? key : '',
        tag: first.tag || '',
        fabric: fabricStr,
        workroom: workroom?.vendor || '',
        pleat: wSpecs.pleat || '',
        mount: wSpecs.mountType || '',
        lining: wSpecs.lining || '',
        finishedWidth: wSpecs.finishedWidth || '',
        finishedLength: wSpecs.finishedLength || '',
        hardware: hardwareStr,
        status: allDone ? 'Complete' : statuses[0] || '',
      };
    });
  }

  function handlePreview() {
    const filtered = getFilteredItems();
    setPreviewItems(filtered);
    setError('');
  }

  async function handleGenerate() {
    const filtered = getFilteredItems();
    if (filtered.length === 0) {
      setError('No client-approved items found for this category and project.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const project = projects.find(p => p.label === selectedProject);
      const projectName = project
        ? `${project.label}${project.city ? '  |  ' + project.city.toUpperCase() + (project.state ? ', ' + project.state.toUpperCase() : '') : ''}`
        : (selectedProject || 'ALL PROJECTS');

      const itemsToSend = selectedCategory === 'Window Treatments'
        ? buildWTAssemblyRows(filtered)
        : filtered;

      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: itemsToSend,
          category: selectedCategory,
          projectName,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'PDF generation failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedProject ? selectedProject + '_' : ''}${selectedCategory.replace(/[^a-zA-Z]/g,'_')}_Schedule.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  }

  const filtered = getFilteredItems();
  const statusCounts = filtered.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Generate Schedules</div>
        <div style={{ fontSize: 13, color: '#666' }}>
          Generate a branded PDF schedule from client-approved items in your database.
        </div>
      </div>

      {/* Controls */}
      <div style={{
        background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10,
        padding: 24, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 5, letterSpacing: 0.3 }}>
              PROJECT
            </label>
            <select value={selectedProject} onChange={e => { setSelectedProject(e.target.value); setPreviewItems(null); }} style={inp}>
              <option value=''>All Projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.label}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 5, letterSpacing: 0.3 }}>
              SCHEDULE TYPE
            </label>
            <select value={selectedCategory} onChange={e => { setSelectedCategory(e.target.value); setPreviewItems(null); }} style={inp}>
              {CATEGORIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handlePreview} style={{
              padding: '8px 18px', background: '#fff', border: '1px solid #ddd',
              borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#444',
            }}>
              Preview Items
            </button>
            <button
              onClick={handleGenerate}
              disabled={loading || filtered.length === 0}
              style={{
                padding: '8px 20px', background: '#1a1a1a', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: (loading || filtered.length === 0) ? 'not-allowed' : 'pointer',
                opacity: (loading || filtered.length === 0) ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {loading ? 'Generating...' : `Download PDF (${filtered.length} items)`}
            </button>
          </div>
        </div>

        {/* Status summary */}
        {filtered.length > 0 && (
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#888', marginRight: 4 }}>INCLUDED:</span>
            {Object.entries(statusCounts).map(([status, count]) => (
              <span key={status} style={{
                background: STATUS_COLORS[status] || '#f5f5f5',
                color: STATUS_TEXT[status] || '#333',
                padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
              }}>
                {status}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Show total items in category regardless of status */}
        {(() => {
          const allInCat = getAllScheduleItems();
          const approvedCount = filtered.length;
          const pendingCount = allInCat.length - approvedCount;
          if (allInCat.length === 0) return (
            <div style={{ marginTop: 12, fontSize: 12, color: '#aaa' }}>
              No {selectedCategory} items found{selectedProject ? ` in ${selectedProject}` : ''}.
              Import items using the Import tab first.
            </div>
          );
          if (approvedCount === 0) return (
            <div style={{ marginTop: 12, fontSize: 12, color: '#aaa' }}>
              {pendingCount} {selectedCategory} item{pendingCount !== 1 ? 's' : ''} found but none are client-approved yet.
              Items need status: Client Approved, Payment Received, Ordered, or Received to appear in schedules.
            </div>
          );
          return null;
        })()}
      </div>

      {error && (
        <div style={{
          background: '#fff5f5', border: '1px solid #fcc', borderRadius: 8,
          padding: '12px 16px', color: '#c0392b', fontSize: 13, marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      {/* Preview table */}
      {previewItems && previewItems.length > 0 && (() => {
        const isWT = selectedCategory === 'Window Treatments';
        const rows = isWT ? buildWTAssemblyRows(previewItems) : previewItems;
        const wtCols = ['Room', 'Assembly', 'Fabric', 'Workroom', 'Pleat', 'Mount', 'Lining', 'W', 'L', 'Hardware', 'Status'];
        const defCols = ['Tag', 'Room', 'Item', 'Vendor', 'Manufacturer', 'Dimensions', 'Finish', 'Status'];
        const cols = isWT ? wtCols : defCols;
        const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: '#fafafa' };
        const td = { padding: '7px 10px', color: '#555', fontSize: 12 };
        return (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ background: '#f5f5f5', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#555', borderBottom: '1px solid #e0e0e0', letterSpacing: 0.5 }}>
              PREVIEW — {isWT ? rows.length + ' ASSEMBLIES' : previewItems.length + ' ' + selectedCategory.toUpperCase() + ' ITEMS'}
              {selectedProject ? ` · ${selectedProject}` : ''}
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 450, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {cols.map(c => <th key={c} style={th}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {isWT ? rows.map((r, idx) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={td}>{r.roomNumber ? r.roomNumber + ' ' : ''}{r.roomName}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{r.assemblyName || '—'}</td>
                      <td style={{ ...td, maxWidth: 180 }}>{r.fabric || '—'}</td>
                      <td style={td}>{r.workroom || '—'}</td>
                      <td style={td}>{r.pleat || '—'}</td>
                      <td style={td}>{r.mount || '—'}</td>
                      <td style={td}>{r.lining || '—'}</td>
                      <td style={td}>{r.finishedWidth || '—'}</td>
                      <td style={td}>{r.finishedLength || '—'}</td>
                      <td style={{ ...td, maxWidth: 140 }}>{r.hardware || '—'}</td>
                      <td style={td}><span style={{ background: STATUS_COLORS[r.status] || '#f5f5f5', color: STATUS_TEXT[r.status] || '#333', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{r.status}</span></td>
                    </tr>
                  )) : previewItems.map((item, idx) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f5f5f5', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...td, fontWeight: 600, fontSize: 11 }}>{item.tag || '—'}</td>
                      <td style={td}>{item.roomNumber ? `${item.roomNumber} ` : ''}{item.roomName}</td>
                      <td style={td}>{item.item}</td>
                      <td style={td}>{item.vendor}</td>
                      <td style={td}>{item.manufacturer}</td>
                      <td style={{ ...td, maxWidth: 140 }}>{item.dimensions}</td>
                      <td style={{ ...td, maxWidth: 140 }}>{item.finish}</td>
                      <td style={td}><span style={{ background: STATUS_COLORS[item.status] || '#f5f5f5', color: STATUS_TEXT[item.status] || '#333', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{item.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {previewItems && previewItems.length === 0 && (
        <div style={{
          border: '1px dashed #ddd', borderRadius: 8, padding: 32,
          textAlign: 'center', color: '#aaa', fontSize: 13,
        }}>
          No client-approved items found for this selection.
        </div>
      )}
    </div>
  );
}
